import React, { useCallback, useEffect } from 'react';

import { Box, Divider, Flex, Text } from '@opendoor/bricks/core';

import { useMutation } from 'platform/apollo';

import { useMobileDevice } from 'shared/helpers/hooks/useMobileDevice';

import { CustomerCareContactCard } from 'buying/components/buyDirectCheckout/CustomerCareContactCard';
import {
  NotifyAepOfContingentBuyer as NotifyAepOfContingentBuyerData,
  NotifyAepOfContingentBuyerVariables,
} from 'buying/graphql/mutations/__generated__/NotifyAepOfContingentBuyer';
import { NotifyAepOfContingentBuyer } from 'buying/graphql/mutations/notifyAepOfContingentBuyer';

interface IContingentSaleStepProps {
  addressId: string | null;
}

const CONTINGENT_BUYER_NOTIFICATION_SENT_KEY = 'opendoor.buyDirect.contingentBuyerNotificationSent';

/**
 * Contingent sale step
 * storybook-check-ignore
 */
export const ContingentSaleStep: React.FC<IContingentSaleStepProps> = React.memo(
  ({ addressId }) => {
    const [notifyAep] = useMutation<
      NotifyAepOfContingentBuyerData,
      NotifyAepOfContingentBuyerVariables
    >(NotifyAepOfContingentBuyer);

    const sendContingentBuyerNotification = useCallback(() => {
      if (!addressId) {
        return;
      }
      let notifiedAboutListings = [];
      const notifiedAboutListingsStr = window.localStorage.getItem(
        CONTINGENT_BUYER_NOTIFICATION_SENT_KEY,
      );
      if (notifiedAboutListingsStr) {
        notifiedAboutListings = JSON.parse(notifiedAboutListingsStr);
      }
      const hasSentNotification: boolean = notifiedAboutListings.includes(addressId);

      if (!hasSentNotification) {
        notifiedAboutListings = notifiedAboutListings.concat(addressId);
        window.localStorage.setItem(
          CONTINGENT_BUYER_NOTIFICATION_SENT_KEY,
          JSON.stringify(notifiedAboutListings),
        );
        notifyAep({ variables: { addressToken: addressId } });
      }
    }, [addressId, notifyAep]);

    useEffect(() => {
      sendContingentBuyerNotification();
    }, [sendContingentBuyerNotification]);

    const { isMobile } = useMobileDevice();
    return (
      <Flex flexDirection="column" alignItems="flex-start">
        <Flex flexDirection="column">
          <Text size={isMobile ? '160' : '150'} fontWeight="medium">
            Tell us more about your sale
          </Text>
          <Text size="110" fontWeight="regular" mb={8} mt={4}>
            We need to learn more about your home sale before you can sign a contract to buy. Call
            or email us.
          </Text>
        </Flex>
        <Box mt={6} mb={10} width="100%">
          <CustomerCareContactCard />
        </Box>
        <Divider width="100%" mb={8} />
        <Flex flexDirection="column">
          <Text size={isMobile ? '160' : '150'} fontWeight="medium">
            Need help with your home sale?
          </Text>
          <Text size="110" fontWeight="regular" mt={4}>
            If you have an agent to help you sell, that’s great. But if you’re looking for one, ask
            our Customer Care team how we can help.
          </Text>
        </Flex>
      </Flex>
    );
  },
);

ContingentSaleStep.displayName = 'ContingentSaleStep';
